import React, { useState } from 'react';
import { Check, X, Trash2, CheckSquare } from 'lucide-react';
import ConfirmationModal from '../ConfirmationModal';
import AlertDialog from '../AlertDialog';

interface Message {
  id: string;
  name: string;
  content: string;
  content_type: 'text';
  created_at: string;
  is_approved: boolean;
}

interface MessageManagementProps {
  messages: Message[];
  onToggleApproval: (id: string, currentStatus: boolean) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  onApproveAll: () => Promise<void>;
}

const MessageManagement: React.FC<MessageManagementProps> = ({
  messages,
  onToggleApproval,
  onDelete,
  onApproveAll
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState<Message | null>(null);
  const [showApproveAllModal, setShowApproveAllModal] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');

  const handleDeleteClick = (message: Message) => {
    setMessageToDelete(message);
    setShowDeleteModal(true);
  };

  const handleApproveAllClick = () => {
    setShowApproveAllModal(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await onDelete(id);
      setAlertMessage('Mensagem excluída com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error deleting message:', error);
      setAlertMessage('Erro ao excluir mensagem. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  const handleToggleApproval = async (id: string, currentStatus: boolean) => {
    try {
      await onToggleApproval(id, currentStatus);
      setAlertMessage(`Mensagem ${currentStatus ? 'desaprovada' : 'aprovada'} com sucesso!`);
      setShowAlert(true);
    } catch (error) {
      console.error('Error toggling message approval:', error);
      setAlertMessage('Erro ao alterar status da mensagem. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  const handleApproveAll = async () => {
    try {
      await onApproveAll();
      setAlertMessage('Todas as mensagens foram aprovadas com sucesso!');
      setShowAlert(true);
    } catch (error) {
      console.error('Error approving all messages:', error);
      setAlertMessage('Erro ao aprovar todas as mensagens. Por favor, tente novamente.');
      setShowAlert(true);
    }
  };

  return (
    <>
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-4 bg-wedding-beige-100 border-b flex justify-between items-center">
          <h3 className="font-medium text-wedding-blue-800">Mensagens aos Noivos</h3>
          {messages.some(m => !m.is_approved) && (
            <button
              onClick={handleApproveAllClick}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
            >
              <CheckSquare size={18} />
              Aprovar Todas
            </button>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nome
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Mensagem
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {messages.map((message) => (
                <tr key={message.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{message.name}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-500 max-w-xs">{message.content}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {new Date(message.created_at).toLocaleDateString('pt-BR')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      message.is_approved
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {message.is_approved ? 'Aprovada' : 'Pendente'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleApproval(message.id, message.is_approved)}
                        className={`p-1 rounded-full transition-colors ${
                          message.is_approved
                            ? 'text-yellow-600 hover:bg-yellow-50'
                            : 'text-green-600 hover:bg-green-50'
                        }`}
                        title={message.is_approved ? 'Desaprovar' : 'Aprovar'}
                      >
                        {message.is_approved ? <X size={18} /> : <Check size={18} />}
                      </button>
                      <button
                        onClick={() => handleDeleteClick(message)}
                        className="p-1 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                        title="Excluir mensagem"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className="p-4 border-t">
          <p className="text-sm text-gray-500">
            Total de mensagens: <span className="font-medium">{messages.length}</span>
          </p>
          <p className="text-sm text-gray-500">
            Mensagens aprovadas: <span className="font-medium">
              {messages.filter(m => m.is_approved).length}
            </span>
          </p>
          <p className="text-sm text-gray-500">
            Mensagens pendentes: <span className="font-medium">
              {messages.filter(m => !m.is_approved).length}
            </span>
          </p>
        </div>
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        title="Excluir Mensagem"
        message="Tem certeza que deseja excluir esta mensagem? Esta ação não pode ser desfeita."
        onClose={() => {
          setShowDeleteModal(false);
          setMessageToDelete(null);
        }}
        onConfirm={() => {
          if (messageToDelete) {
            handleDelete(messageToDelete.id);
            setShowDeleteModal(false);
            setMessageToDelete(null);
          }
        }}
      />

      <ConfirmationModal
        isOpen={showApproveAllModal}
        title="Aprovar Todas as Mensagens"
        message="Tem certeza que deseja aprovar todas as mensagens pendentes?"
        onClose={() => setShowApproveAllModal(false)}
        onConfirm={() => {
          handleApproveAll();
          setShowApproveAllModal(false);
        }}
      />

      <AlertDialog
        isOpen={showAlert}
        title="Notificação"
        message={alertMessage}
        onClose={() => setShowAlert(false)}
      />
    </>
  );
};

export default MessageManagement;